// src/components/seller/sellertopbar/SellerTopbar.js
import React, { useContext, useEffect, useRef, useState } from "react";

import { FiMoon, FiSearch, FiSun, FiBell, FiMenu, FiUser, FiLock, FiLogOut, FiChevronDown, FiUserX } from "react-icons/fi";


import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { useTheme } from "../../../contexts/ThemeContext";
import "../../../assets/css/seller/components/sellertopbar/SellerTopbar.css";
import AppContext from "../../../contexts/AppContext";

const SellerTopbar = ({ onToggleSidebar }) => {
  const { isDark, toggleTheme } = useTheme();
  const { seller, baseUrl, unreadCounts, setUnreadCount, clearUnread,sellerAuthToken } =
    useContext(AppContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  const sellerName = seller?.full_name || "";
  const sellerEmail = seller?.email || "";
  const sellerImage = null;

  // Fetch unread notification count on mount
  useEffect(() => {
    if (!seller?.id) return;
    axios
      .post(`${baseUrl}/seller/order/getSellerNotifications`, {
        chseller_id: seller.id,
      },{
              headers: {
                Authorization: `Bearer ${sellerAuthToken}`,
              },
            },
    )
      .then((res) => {
        const notifications = res.data.notifications || [];
        const unreadCount = notifications.filter(
          (n) => n.status === "unread",
        ).length;
        setUnreadCount("notifications", unreadCount);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const getTitle = () => {
    const p = location.pathname;
    if (p.includes("change-password")) return "Change Password";
    if (p.includes("profile")) return "My Profile";
    if (p.includes("dashboard")) return "Dashboard";
    if (p.includes("messages")) return "Messages";
    if (p.includes("products")) return "Products";
    if (p.includes("orders")) return "Orders";
    if (p.includes("earnings")) return "TopUp";
    if (p.includes("reporting")) return "Reporting";
    if (p.includes("support")) return "Support";
    if (p.includes("settings")) return "Settings";
    if (p.includes("editstore")) return "Edit Store";
    if (p.includes("notifications")) return "Notifications";
    return "Dashboard";
  };

  const handleLogout = () => {
    localStorage.removeItem("loggedInSeller");
    localStorage.removeItem("seller_id");
    localStorage.removeItem("sellerAuthToken");
    navigate("/seller/login");
  };

  const handleDeleteAccount = () => {
    setOpen(false);
    Swal.fire({
      title: "Delete Account",
      text: "Are you sure you want to delete your seller account? You will not be able to login again, and this email cannot be used to create a new account.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete My Account",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "delete-sweet-alert",
        confirmButton: "delete-confirm-btn",
        cancelButton: "delete-cancel-btn"
      }
    }).then((result) => {
      if (result.isConfirmed) {
      
        axios
          .post(`${baseUrl}/seller/deleteAccount`, { seller_id: seller?.id },
            {
              headers: {
                Authorization: `Bearer ${sellerAuthToken}`,
              },
            },
          )
          .then(() => {
            Swal.fire({
              title: "Account Deleted",
              text: "Your account has been deleted successfully.",
              icon: "success",
              confirmButtonColor: "#FF8559",
              timer: 1800,
              showConfirmButton: false
            }).then(() => {
              handleLogout();
            });
          })
          .catch((error) => {
            Swal.fire({
              title: "Something went wrong",
              text: error.response?.data?.message || "Unable to delete account. Please try again.",
              icon: "error",
              confirmButtonColor: "#FF8559"
            });
          });
      }
    });
  };

  const handleBellClick = () => {
    clearUnread("notifications");
    navigate("/seller/notifications");
  };

  const getInitials = (name) => {
    if (!name) return null;
    if (name.includes("@")) return name.charAt(0).toUpperCase();
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className={`seller-topbar ${isDark ? "dark-mode" : ""}`}>
      {/* LEFT: Hamburger + Page Title */}
      <div className="seller-topbar-left">
        <button
          className="seller-topbar-hamburger"
          onClick={onToggleSidebar}
          aria-label="Toggle menu"
        >
          <FiMenu />
        </button>
        <h2 className="seller-topbar-title">{getTitle()}</h2>
      </div>

      {/* RIGHT */}
      <div className="seller-topbar-right">
        {/* Search */}
        {/* <div className="seller-search">
          <FiSearch />
          <input placeholder="Search" />
        </div> */}

        <div className="seller-top-actions">
          {/* Theme toggle */}
          <div
            className={`seller-toggle-pill ${isDark ? "on" : ""}`}
            onClick={toggleTheme}
          >
            <FiSun className={!isDark ? "icon-active" : ""} />
            <FiMoon className={isDark ? "icon-active" : ""} />
          </div>

          {/* Bell with Badge */}
          <button
            className="seller-topbar-bell-btn"
            onClick={handleBellClick}
            style={{ position: "relative" }}
          >
            <FiBell />
            {unreadCounts.notifications > 0 && (
              <span
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  background: "#ef4444",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: "700",
                  borderRadius: "50%",
                  minWidth: "17px",
                  height: "17px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  padding: "0 3px",
                  pointerEvents: "none",
                }}
              >
                {unreadCounts.notifications > 99
                  ? "99+"
                  : unreadCounts.notifications}
              </span>
            )}
          </button>

          {/* Profile dropdown */}
          <div className="seller-profile-wrapper" ref={dropRef}>
            <button
              className="seller-profile-btn"
              onClick={() => setOpen((p) => !p)}
            >
              <div className="seller-tb-avatar">
                {sellerImage ? (
                  <img
                    src={sellerImage}
                    alt="Profile"
                    className="seller-tb-avatar-img"
                  />
                ) : getInitials(sellerName) ? (
                  <span className="seller-tb-initials">
                    {getInitials(sellerName)}
                  </span>
                ) : (
                  <FiUser size={16} color="#ff8559" />
                )}
              </div>
              <div className="seller-tb-info">
                <span className="seller-tb-name">{sellerName}</span>
                <span className="seller-tb-email">{sellerEmail}</span>
              </div>

              <FiChevronDown
                size={12}
                className={`seller-tb-chevron ${open ? "open" : ""}`}
                style={{
                  transition: "transform 0.2s",
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            {open && (
              <div className={`seller-dropdown ${isDark ? "dark-mode" : ""}`}>
                <button
                  className="seller-dd-item"
                  onClick={() => {
                    navigate("/seller/profile");
                    setOpen(false);
                  }}
                >
                  <span className="seller-dd-icon">
                    <FiUser size={14} />
                  </span>{" "}
                  My Profile
                </button>

                <button
                  className="seller-dd-item"
                  onClick={() => {
                    navigate("/seller/change-password");
                    setOpen(false);
                  }}
                >
                  <span className="seller-dd-icon">
                    <FiLock size={14} />
                  </span>{" "}
                  Change Password
                </button>

                <div className="seller-dd-divider" />

                <button
                  className="seller-dd-item seller-dd-logout"
                  onClick={handleLogout}
                >
                  <span className="seller-dd-icon">
                    <FiLogOut size={14} />
                  </span>{" "}
                  Logout
                </button>


                <button className="seller-dd-item seller-dd-delete" onClick={handleDeleteAccount}>
                  <span className="seller-dd-icon"><FiUserX size={14} /></span> Delete Account
                </button>


              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default SellerTopbar;
